import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, User, Phone, Building, Layers, MapPin, Check, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CompleteProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [form, setForm] = useState({
    whatsapp: '',
    company: '',
    floor: '',
    work_area: ''
  });
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [selfieBase64, setSelfieBase64] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen no debe superar 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelfiePreview(e.target.result);
        setSelfieBase64(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.whatsapp || !form.company || !form.floor || !form.work_area) {
      toast.error('Todos los campos son obligatorios');
      return;
    }
    
    if (!selfieBase64) {
      toast.error('La selfie es obligatoria');
      return;
    }
    
    setLoading(true);
    
    try {
      // Upload selfie first
      const selfieResponse = await fetch(`${API}/upload/selfie`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image: selfieBase64 })
      });
      
      if (!selfieResponse.ok) {
        throw new Error('Error al subir la selfie');
      }
      
      const selfieData = await selfieResponse.json();
      
      // Update profile
      const profileResponse = await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          whatsapp: form.whatsapp,
          selfie_url: selfieData.selfie_url,
          company: form.company,
          floor: form.floor,
          work_area: form.work_area
        })
      });
      
      if (!profileResponse.ok) {
        throw new Error('Error al actualizar el perfil');
      }
      
      const updatedUser = await profileResponse.json();
      setUser(updatedUser);
      
      toast.success('¡Perfil completado exitosamente!');
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Profile error:', error);
      toast.error(error.message || 'Error al completar el perfil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-3xl border border-stone-200 p-8 shadow-xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-emerald-700" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">Completa tu Perfil</h1>
            <p className="text-stone-500">Necesitamos algunos datos para procesar tus pedidos</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Selfie Upload */}
            <div className="flex flex-col items-center">
              <div 
                className="w-32 h-32 rounded-full bg-stone-100 border-2 border-dashed border-stone-300 flex items-center justify-center cursor-pointer overflow-hidden hover:border-emerald-500 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="selfie-upload"
              >
                {selfiePreview ? (
                  <img src={selfiePreview} alt="Selfie" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <Camera className="w-8 h-8 text-stone-400 mx-auto mb-1" />
                    <span className="text-xs text-stone-500">Subir Selfie</span>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="user"
                onChange={handleFileChange}
                className="hidden"
              />
              <p className="text-xs text-stone-500 mt-2">Toca para tomar o subir una foto</p>
            </div>

            {/* WhatsApp */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                <Phone className="w-4 h-4" />
                WhatsApp
              </label>
              <Input
                type="tel"
                placeholder="55 1234 5678"
                value={form.whatsapp}
                onChange={(e) => setForm({...form, whatsapp: e.target.value})}
                className="bg-stone-50 border-stone-200 rounded-xl h-12"
                data-testid="whatsapp-input"
              />
            </div>

            {/* Company */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                <Building className="w-4 h-4" />
                Empresa
              </label>
              <Input
                placeholder="Ej: Kapital Grupo Financiero"
                value={form.company}
                onChange={(e) => setForm({...form, company: e.target.value})}
                className="bg-stone-50 border-stone-200 rounded-xl h-12"
                data-testid="company-input"
              />
            </div>

            {/* Floor */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                <Layers className="w-4 h-4" />
                Piso
              </label>
              <Input
                placeholder="Ej: Piso 24"
                value={form.floor}
                onChange={(e) => setForm({...form, floor: e.target.value})}
                className="bg-stone-50 border-stone-200 rounded-xl h-12"
                data-testid="floor-input"
              />
            </div>

            {/* Work Area */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                <MapPin className="w-4 h-4" />
                Zona de Trabajo
              </label>
              <Input
                placeholder="Ej: Zona de Crédito"
                value={form.work_area}
                onChange={(e) => setForm({...form, work_area: e.target.value})}
                className="bg-stone-50 border-stone-200 rounded-xl h-12"
                data-testid="work-area-input"
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full py-6 text-lg font-medium"
              disabled={loading}
              data-testid="complete-profile-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  Guardando...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" />
                  Completar Perfil
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
